import { zodResolver } from "@hookform/resolvers/zod";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import React, { useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { TypeOf, z } from "zod";
import { Profile } from "../../../../../@types/profile";
import DelendCentralizedCircularProgress from "../../../../../components/CircularProgress/DelendCentralizedCircularProgress";
import PasswordHide from "../../../../../components/Vectors/PasswordHide";
import PasswordVisible from "../../../../../components/Vectors/PasswordVisible";
import { useAppSelector } from "../../../../../hooks/hooks";
import UserService from "../../../../../services/UserService";
import ReturnButton from "../../ReturnButton";
import { useTheme } from "@mui/material/styles";

const passwordInput = z
  .object({
    oldPassword: z.string().nonempty({ message: "senha atual é obrigatória" }),
    newPassword: z.string().nonempty({ message: "nova senha é obrigatória" }),
    newPasswordConfirm: z
      .string()
      .nonempty({ message: "confirmação da nova senha é obrigatória" }),
  })
  .refine((schema) => schema.newPassword === schema.newPasswordConfirm, {
    message: "as senhas não conferem",
    path: ["newPasswordConfirm"],
  });

type PasswordInput = TypeOf<typeof passwordInput>;

const ProfileHeader = () => {
  return (
    <Box display={"flex"} flexDirection={"column"} gap={1} mb={2}>
      <Typography variant="h2">Alterar senha</Typography>

      <Typography>
        Sua senha nova senha deve ser diferente da senha atual e deve conter
        mais de 6 caracteres
      </Typography>
    </Box>
  );
};

const DelendPasswordField = (props: any) => {
  const [showPassword, setShowPassword] = useState(false);
  const handleClickShowPassword = () => setShowPassword(!showPassword);
  const handleMouseDownPassword = () => setShowPassword(!showPassword);

  return (
    <TextField
      error={props.error}
      {...props.validation}
      helperText={props.helperText ? props.helperText : " "}
      label={props.label}
      fullWidth
      sx={{ mb: 2 }}
      onChange={props.onChange}
      color="secondary"
      variant="standard"
      type={showPassword ? "text" : "password"}
      InputProps={{
        endAdornment: (
          <InputAdornment sx={{ marginRight: "-5px" }} position="end">
            <IconButton
              aria-label="toggle password visibility"
              onClick={handleClickShowPassword}
              onMouseDown={handleMouseDownPassword}
              sx={{ "&:hover": { backgroundColor: "transparent" } }}
            >
              {showPassword ? (
                <PasswordVisible text={props.value} />
              ) : (
                <PasswordHide text={props.value} />
              )}
            </IconButton>
          </InputAdornment>
        ),
      }}
    />
  );
};

const PasswordEditor: React.FC = () => {
  const navigate = useNavigate();

  const profile = useAppSelector<Profile | undefined>(
    (state) => state.profileSlice.profile
  );

  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const [evaluatingPasswordStrength, setEvaluatingPasswordStrength] =
    useState("");

  const {
    register,
    clearErrors,
    formState: { errors, isSubmitting },
    handleSubmit,
  } = useForm<PasswordInput>({
    resolver: zodResolver(passwordInput),
  });

  const handleTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.name === "newPassword") {
      setEvaluatingPasswordStrength(event.target.value);
    }
    const newValue = event.target.value;
    clearErrors("oldPassword");
    setIsButtonDisabled(newValue === profile?.person.name);
  };

  if (!profile) return <DelendCentralizedCircularProgress />;

  const onSubmit: SubmitHandler<PasswordInput> = async (passwords) => {
    try {
      await UserService.changePassword(
        passwords.oldPassword,
        passwords.newPassword
      );

      toast.success("Senha alterada com sucesso!");
      navigate("/dashboard/profile");
    } catch (error) {
      toast.error("" + error);
    }
  };

  const PasswordStrength = () => {
    const theme = useTheme();
    const COLORS = {
      NEUTRAL: theme.palette.grey[100],
      WEAK: theme.palette.error.main,
      MEDIUM: theme.palette.warning.main,
      STRONG: theme.palette.success.light,
    };

    const defineStrength = (): { bar: string[]; description: string } => {
      //weak conditions
      if (evaluatingPasswordStrength.length < 6) {
        setIsButtonDisabled(true);
        return {
          bar: [COLORS.WEAK, COLORS.NEUTRAL, COLORS.NEUTRAL, COLORS.NEUTRAL],
          description: "Senha fraca",
        };
      }
      //medium conditions
      if (
        evaluatingPasswordStrength.length >= 6 &&
        evaluatingPasswordStrength.length <= 10
      ) {
        setIsButtonDisabled(false);
        return {
          bar: [COLORS.MEDIUM, COLORS.MEDIUM, COLORS.NEUTRAL, COLORS.NEUTRAL],
          description: "Senha intermediária",
        };
      }
      //Strong conditions
      if (evaluatingPasswordStrength.length > 10) {
        setIsButtonDisabled(false);
        return {
          bar: [COLORS.STRONG, COLORS.STRONG, COLORS.STRONG, COLORS.STRONG],
          description: "Senha forte",
        };
      }
      //default conditions
      return {
        bar: [COLORS.NEUTRAL, COLORS.NEUTRAL, COLORS.NEUTRAL, COLORS.NEUTRAL],
        description: "",
      };
    };

    return (
      evaluatingPasswordStrength && (
        <React.Fragment>
          <Typography variant="caption">
            {defineStrength().description}
          </Typography>

          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            width={"50%"}
            gap="5px"
            mb={4}
          >
            {defineStrength().bar.map((color, index) => (
              <Box
                key={index}
                flex={1}
                height="5px"
                borderRadius="5px"
                bgcolor={color}
              />
            ))}
          </Box>
        </React.Fragment>
      )
    );
  };

  return (
    <React.Fragment>
      <Box display={"flex"} maxWidth={"100%"} gap={2}>
        <ReturnButton to="/dashboard/profile" />
        <Box
          display={"flex"}
          flexDirection={"column"}
          gap={2}
          width={"100%"}
          component="form"
          noValidate
          autoComplete="off"
          onSubmit={handleSubmit(onSubmit)}
        >
          <ProfileHeader />
          <DelendPasswordField
            validation={{ ...register("oldPassword") }}
            error={!!errors.oldPassword}
            onChange={handleTextChange}
            helperText={errors.oldPassword?.message}
            label="Senha atual"
            disabled={isSubmitting}
          />
          <DelendPasswordField
            validation={{ ...register("newPassword") }}
            error={!!errors.newPassword}
            onChange={handleTextChange}
            helperText={errors.newPassword?.message}
            label={"Nova senha"}
            disabled={isSubmitting}
          />
          <PasswordStrength />
          <DelendPasswordField
            validation={{ ...register("newPasswordConfirm") }}
            error={!!errors.newPasswordConfirm}
            onChange={handleTextChange}
            helperText={errors.newPasswordConfirm?.message}
            label={"Confirmar senha"}
            disabled={isSubmitting}
          />
          <Box display={"flex"} justifyContent={"end"}>
            <Typography style={{ marginTop: 6 }} mr={4} align="right">
              <Link
                className="forgotPassword"
                to="/dashboard/profile/edit/password/reset"
              >
                Esqueceu sua senha?
              </Link>
            </Typography>

            <Button
              variant="outlined"
              size="large"
              disabled={isButtonDisabled || isSubmitting}
              type="submit"
              endIcon={isSubmitting && <CircularProgress size={20} />}
            >
              Alterar senha
            </Button>
          </Box>
        </Box>
      </Box>
    </React.Fragment>
  );
};

export default PasswordEditor;
